import { useMemo, useState } from 'react';
import { Loader2, Plus, FileSpreadsheet, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/utils';
import { useBreakdown } from '@/hooks/useBreakdown';
import { useSubItemCategoriesContext } from '@/contexts/SubItemCategoriesContext';
import { downloadQuickBooksCsv } from '@/lib/quickbooks-csv';
import {
  useJobInvoices,
  useUpdateInvoice,
  useDeleteInvoice,
  useMarkInvoiceSent,
  downloadInvoicePdf,
} from '@/hooks/useInvoices';
import { useToast } from '@/hooks/use-toast';
import { InvoiceTable } from '@/components/billing/InvoiceTable';
import { InvoiceCards } from '@/components/billing/InvoiceCards';
import { fromInvoice, JOB_COLUMNS } from '@/components/billing/invoiceRow';
import type { BillingInvoiceRow } from '@/components/billing/invoiceRow';
import type { InvoiceRowActionHandlers } from '@/components/billing/InvoiceTable';
import { PaymentsPanel } from '@/components/billing/PaymentsPanel';
import { SendInvoiceDialog } from '@/components/billing/SendInvoiceDialog';
import { InvoicePdfViewerDialog } from '@/components/billing/InvoicePdfViewerDialog';
import { JobBillingStateBadge } from '@/components/billing/JobBillingStateBadge';
import { useJobBilling } from '@/hooks/useInvoicePayments';
import { PartialInvoiceDialog } from './PartialInvoiceDialog';
import { FinalInvoiceDialog } from './FinalInvoiceDialog';
import { InvoiceNotesPopover } from './InvoiceNotesPopover';
import { InvoicePreview } from './InvoicePreview';
import { RegenerateInvoiceDialog } from './RegenerateInvoiceDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import type { Invoice } from '@jobos/shared';

/**
 * One job's billing. The SAME components the /invoices workspace renders,
 * pointed at a single job — which is why W2 and W3 are one module rather than
 * two: the job screens are not extra work.
 *
 * What stays job-specific is what genuinely is: the QuickBooks CSV export, the
 * invoice preview and the regenerate dialog, all of which need the job's
 * breakdown.
 */

interface JobDetailBillingProps {
  jobId: string;
  jobCode: string;
  jobDescription: string | null;
  clientName: string;
}

export function JobDetailBilling({
  jobId,
  jobCode,
  jobDescription,
  clientName,
}: JobDetailBillingProps) {
  const { data: breakdown, isLoading: breakdownLoading } = useBreakdown(jobId);
  const { data: invoices = [], isLoading: invoicesLoading } = useJobInvoices(jobId);
  // FR-1.5: the money figures are derived server-side from the payment ledger.
  // Never re-summed here — two implementations of "how much have we collected"
  // is exactly the drift spec D6 exists to prevent.
  const { data: billing } = useJobBilling(jobId);
  const updateInvoice = useUpdateInvoice(jobId);
  const markSent = useMarkInvoiceSent(jobId);
  const deleteInvoice = useDeleteInvoice(jobId);
  const { toast } = useToast();

  const { categories, typeMeta } = useSubItemCategoriesContext();

  const [partialDialogOpen, setPartialDialogOpen] = useState(false);
  const [finalDialogOpen, setFinalDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Which invoice the regenerate confirm dialog is for; null = closed. Finals
  // only — a partial is edited instead.
  const [regenerating, setRegenerating] = useState<Invoice | null>(null);
  // Which partial the creation dialog is open on in EDIT mode; null = closed.
  // Separate state from `regenerating`, but the two can never be open together:
  // the action menu resolves one handler per invoice, never both.
  const [editingPartial, setEditingPartial] = useState<Invoice | null>(null);
  // Which invoice the payments panel is open for; null = closed.
  const [payingFor, setPayingFor] = useState<Invoice | null>(null);
  const [recordOpen, setRecordOpen] = useState(false);
  // Same shape for the send dialog: which invoice it is open for, null = closed.
  const [sendingFor, setSendingFor] = useState<Invoice | null>(null);
  // And for the PDF viewer.
  const [viewingPdfFor, setViewingPdfFor] = useState<Invoice | null>(null);

  const contractValue = billing?.contractValue ?? breakdown?.totalCost ?? 0;

  // The shared components' row shape. Keeping the Invoice alongside it means
  // the dialogs below still receive the payload they already expect.
  const rows = useMemo(() => invoices.map(fromInvoice), [invoices]);
  const byId = useMemo(() => new Map(invoices.map((i) => [i.id, i])), [invoices]);

  const openPayments = (invoice: Invoice, startRecording: boolean) => {
    setPayingFor(invoice);
    setRecordOpen(startRecording);
  };

  const handleDownloadPdf = async (invoice: Invoice) => {
    // The menu closes on click, so surface progress as a toast — the cached PDF
    // is fetched server-side, which can take a beat.
    const progress = toast({ title: 'Downloading PDF…' });
    try {
      await downloadInvoicePdf(invoice.id, invoice.invoiceNumber);
    } catch {
      toast({
        title: 'Download failed',
        description: 'Could not download the invoice PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      progress.dismiss();
    }
  };

  const actionsFor = (row: BillingInvoiceRow): InvoiceRowActionHandlers => {
    const invoice = byId.get(row.invoiceId)!;
    return {
      onRecordPayment: () => openPayments(invoice, true),
      onViewPayments: () => openPayments(invoice, false),
      // `void` the returned promises: the menu's handlers report completion,
      // not the mutation's payload, and each mutation toasts for itself.
      onMarkSent: () => void markSent.mutateAsync(invoice.id),
      // The sent date, corrected by hand — for the invoice that was texted or
      // handed over and so was never stamped. Not silent: unlike a note, this
      // one moves the invoice between Draft and Sent.
      onSetSentDate: (date) => void updateInvoice.mutateAsync({ id: invoice.id, sentAt: date }),
      onSend: () => setSendingFor(invoice),
      onVoid: () => void updateInvoice.mutateAsync({ id: invoice.id, status: 'voided' }),
      onDelete: () => void deleteInvoice.mutateAsync(invoice.id),
      onViewPdf: () => setViewingPdfFor(invoice),
      onDownloadPdf: () => handleDownloadPdf(invoice),
      // FINALS reach this one. Only this screen passes it — without it
      // RegenerateInvoiceDialog would have no trigger and be dead code.
      onRegenerate: () => setRegenerating(invoice),
      // EVERY PARTIAL reaches this one, line-item and free-form alike. Mutually
      // exclusive with onRegenerate in the menu — see InvoiceRowActions.
      onEditPartial: () => setEditingPartial(invoice),
    };
  };

  const notesCellFor = (row: BillingInvoiceRow) => (
    <InvoiceNotesPopover invoiceId={row.invoiceId} jobId={jobId} notes={row.notes} />
  );

  const handleExportQuickBooks = () => {
    if (!breakdown) return;

    const allLineItems = [
      ...breakdown.lineItems,
      ...breakdown.projects.flatMap((p) => p.lineItems),
    ];

    downloadQuickBooksCsv({
      lineItems: allLineItems,
      rates: { taxRate: breakdown.taxRate, markupRate: breakdown.markupRate },
      typeMeta,
      categories,
      clientName,
      jobCode,
    });
  };

  if (breakdownLoading || invoicesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const emptyState = (
    <div className="rounded-lg border bg-white p-12 text-center">
      <p className="text-gray-500">No invoices yet</p>
    </div>
  );

  const listProps = {
    rows,
    columns: JOB_COLUMNS,
    actionsFor,
    notesCellFor,
    emptyState,
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Rollup header — server-derived (FR-1.5) */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <RollupCard label="Contract value" value={contractValue} hint="From breakdown" />
        <RollupCard label="Invoiced" value={billing?.invoiced ?? 0} hint="Sent invoices only" />
        <RollupCard label="Collected" value={billing?.collected ?? 0} valueClass="text-green-600" />
        <RollupCard
          label="Outstanding"
          value={billing?.outstanding ?? 0}
          valueClass="text-red-600"
          footer={<JobBillingStateBadge state={billing?.billingState} />}
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => setFinalDialogOpen(true)}
          className="bg-blue-900 text-white hover:bg-blue-800"
        >
          <Plus className="mr-2 h-4 w-4" />
          Final Invoice
        </Button>
        <Button variant="outline" onClick={() => setPreviewOpen(true)}>
          <Eye className="mr-2 h-4 w-4" />
          Preview Invoice
        </Button>
        <Button variant="outline" onClick={() => setPartialDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Partial Invoice
        </Button>
        <Button
          variant="outline"
          onClick={handleExportQuickBooks}
          disabled={
            !breakdown ||
            (breakdown.lineItems.length === 0 &&
              breakdown.projects.every((p) => p.lineItems.length === 0))
          }
        >
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          QuickBooks CSV
        </Button>
      </div>

      {/* Invoice preview modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Invoice Preview</DialogTitle>
            <DialogDescription>
              Use the <span className="font-medium">Itemized</span> switch beside each line to show
              or hide its sub-item breakdown on the invoice. Switch off to show only the line and
              its total. The switch in the column heading sets every line at once, and individual
              lines can still be adjusted afterwards. Changes save automatically.
            </DialogDescription>
          </DialogHeader>
          {/* Mount only when open so the preview fetches lazily. */}
          {previewOpen && <InvoicePreview jobId={jobId} />}
        </DialogContent>
      </Dialog>

      {/* The same list the workspace renders, minus the client and job columns
          this screen is already scoped to. */}
      <div className="hidden lg:block">
        <InvoiceTable {...listProps} />
      </div>
      <div className="lg:hidden">
        <InvoiceCards {...listProps} />
      </div>

      <PartialInvoiceDialog
        open={partialDialogOpen}
        onOpenChange={setPartialDialogOpen}
        jobId={jobId}
        jobCode={jobCode}
        jobDescription={jobDescription}
      />
      {/* THE SAME DIALOG, editing. Separate instance rather than one with a
          nullable invoice: Create and Edit are opened from different places and
          must not share a half-filled form. Mounted only while editing, so
          closing unmounts the form rather than leaving last time's answers in
          it, and `key` forces a fresh mount when the menu swaps invoices
          without closing. */}
      {editingPartial && (
        <PartialInvoiceDialog
          key={editingPartial.id}
          open
          onOpenChange={(open) => !open && setEditingPartial(null)}
          jobId={jobId}
          jobCode={jobCode}
          jobDescription={jobDescription}
          invoice={editingPartial}
        />
      )}

      <RegenerateInvoiceDialog
        invoice={regenerating}
        onOpenChange={(open) => !open && setRegenerating(null)}
        jobId={jobId}
      />

      {/* The shared ledger panel — the workspace opens the same one. */}
      <PaymentsPanel
        key={payingFor?.id}
        invoice={
          payingFor && {
            invoiceId: payingFor.id,
            jobId,
            invoiceNumber: payingFor.invoiceNumber,
            amount: payingFor.amount,
            collected: payingFor.collected,
            outstanding: payingFor.outstanding,
            lifecycle: payingFor.lifecycle,
            amountUnknown: payingFor.origin === 'external' && payingFor.amount === 0,
          }
        }
        startRecording={recordOpen}
        onClose={() => setPayingFor(null)}
      />

      {/* The same viewer the workspace opens. Keyed so a second invoice fetches
          its own PDF rather than showing the last one's while the new bytes are
          still in flight. */}
      <InvoicePdfViewerDialog
        key={viewingPdfFor?.id}
        invoice={
          viewingPdfFor && {
            invoiceId: viewingPdfFor.id,
            invoiceNumber: viewingPdfFor.invoiceNumber,
          }
        }
        onClose={() => setViewingPdfFor(null)}
      />

      {/* The same dialog the workspace opens. Mounted only while open — it asks
          the server to compose an email preview — and keyed so a second invoice
          starts from its own draft. The hooks invalidate this page's caches, so
          unlike the workspace there is no extra onSent to pass. */}
      {sendingFor && (
        <SendInvoiceDialog
          key={sendingFor.id}
          open
          onOpenChange={(open) => !open && setSendingFor(null)}
          invoiceId={sendingFor.id}
          invoiceNumber={sendingFor.invoiceNumber}
          jobId={jobId}
        />
      )}

      <FinalInvoiceDialog
        open={finalDialogOpen}
        onOpenChange={setFinalDialogOpen}
        jobId={jobId}
        jobCode={jobCode}
        jobDescription={jobDescription}
      />
    </div>
  );
}

function RollupCard({
  label,
  value,
  hint,
  valueClass,
  footer,
}: {
  label: string;
  value: number;
  hint?: string;
  valueClass?: string;
  footer?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-white p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${valueClass ?? ''}`}>{formatCurrency(value)}</p>
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
      {footer && <div className="mt-1">{footer}</div>}
    </div>
  );
}
